import { X } from 'lucide-react'
import type { User } from '../../modules/auth/entities/User'
import { customerNavItems } from './navConfig'
import CustomerRightSideBarSystemInfo from './CustomerRightSideBarSystemInfo'
import CustomerRightSideBarQuickActions from './CustomerRightSideBarQuickActions'
import UserAvatar from '../common/UserAvatar'

type Props = {
  activeKey: string
  onNavigateKey: (key: string) => void
  onOpenAuth: () => void
  mobileOpen: boolean
  onCloseMobile: () => void
  user: User | null
  isInitializing: boolean
}

export default function CustomerRightSideBar({ activeKey, onNavigateKey, onOpenAuth, mobileOpen, onCloseMobile, user, isInitializing }: Props) {
  const renderNav = (mode: 'desktop' | 'mobile') => {
    return (
      <nav className="p-2 space-y-1">
        {customerNavItems.map((item) => {
          const Icon = item.icon
          const isActive = activeKey === item.key

          const onClick = () => {
            if (isInitializing) return
            if (item.requiresAuth && !user) {
              onOpenAuth()
              if (mode === 'mobile') onCloseMobile()
              return
            }
            onNavigateKey(item.key)
            if (mode === 'mobile') onCloseMobile()
          }

          return (
            <button
              key={item.key}
              type="button"
              onClick={onClick}
              className={
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 '
                + (isActive
                  ? 'bg-gradient-to-r from-amber-500 via-orange-600 to-rose-600 text-white shadow-[0_14px_34px_rgba(249,115,22,0.25)]'
                  : 'text-slate-600 dark:text-slate-200/90 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white')
              }
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              <span className="text-[15px] font-semibold tracking-wide">{item.label}</span>
            </button>
          )
        })}
      </nav>
    )
  }

  return (
    <>
      <aside className="hidden md:block w-72 shrink-0 self-start sticky top-6">
        <div>
          <div className="sidebar-surface rounded-2xl shadow-[0_24px_60px_rgba(2,6,23,0.12)] overflow-hidden">
            {/* Logo + Title */}
            <div className="px-4 py-4 border-b border-slate-200/80 dark:border-white/10">
              <div className="flex items-center gap-3">
                <img
                  src="/DigiGO.png"
                  alt="Tạp hóa nội dung số"
                  className="h-11 w-11 rounded-xl object-cover ring-2 ring-amber-400/30 shadow-md"
                />
                <div className="min-w-0">
                  <div className="text-lg font-extrabold tracking-tight text-slate-900 dark:text-white">
                    Tạp hóa nội dung số
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                    Nơi mua tool AI nhanh, gọn, giá tốt
                  </div>
                </div>
              </div>
            </div>

            {/* User Card */}
            <div className="px-4 py-3 border-b border-slate-200/80 dark:border-white/10 bg-gradient-to-br from-amber-50/50 to-orange-50/30 dark:from-amber-900/10 dark:to-orange-900/5">
              {user ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <UserAvatar
                      src={user.avatar_url}
                      name={user.full_name || user.username || user.email}
                      alt={user.full_name || user.username || 'Avatar người dùng'}
                      containerClassName="h-11 w-11 rounded-xl border border-amber-200/80 dark:border-amber-400/20 shadow-sm"
                      iconClassName="text-sm"
                    />
                    <div className="min-w-0">
                      <div className="text-sm text-slate-600 dark:text-slate-300">
                        Xin chào, <span className="font-bold text-slate-900 dark:text-white">{user.full_name || user.username || 'Quý khách'}</span>!
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 dark:text-slate-400">Số dư hiện tại:</span>
                    <span className="text-base font-extrabold text-amber-600 dark:text-amber-400">
                      {(user.balance || 0).toLocaleString('vi-VN')}đ
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-sm text-slate-600 dark:text-slate-300">
                    Chào mừng quý khách!
                  </div>
                  <button
                    type="button"
                    onClick={onOpenAuth}
                    className="w-full py-2 px-4 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-md transition-all"
                  >
                    Đăng nhập / Đăng ký
                  </button>
                </div>
              )}
            </div>

            {/* Navigation Title */}
            <div className="px-4 py-2 border-b border-slate-200/80 dark:border-white/10">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Điều hướng</div>
            </div>

            {renderNav('desktop')}
            <CustomerRightSideBarSystemInfo />
            <CustomerRightSideBarQuickActions
              isAuthenticated={!!user}
              onOpenAuth={onOpenAuth}
            />
          </div>
        </div>
      </aside>

      {mobileOpen ? (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onCloseMobile} />
          <aside className="absolute inset-y-0 right-0 w-[86vw] max-w-[360px] p-3">
            <div className="sidebar-surface rounded-2xl shadow-[0_24px_60px_rgba(2,6,23,0.18)] overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200/80 dark:border-white/10 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {user ? (
                      <UserAvatar
                        src={user.avatar_url}
                        name={user.full_name || user.username || user.email}
                        alt={user.full_name || user.username || 'Avatar người dùng'}
                        containerClassName="h-7 w-7 rounded-lg border border-slate-200/80 dark:border-white/10"
                        iconClassName="text-[10px]"
                      />
                    ) : null}
                    <div className="text-sm font-bold tracking-wide text-slate-900 dark:text-white whitespace-nowrap">Mục lục</div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-300 truncate">
                      {user ? (user.full_name || user.username || 'Người dùng') : 'Khách'}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onCloseMobile}
                  className="h-9 w-9 grid place-items-center rounded-xl border border-slate-200/80 dark:border-white/10 bg-slate-50 dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 text-slate-600 dark:text-white/90 transition-colors"
                  aria-label="Đóng"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {renderNav('mobile')}
              <CustomerRightSideBarSystemInfo />
              <CustomerRightSideBarQuickActions
                isAuthenticated={!!user}
                onOpenAuth={onOpenAuth}
                onActionComplete={onCloseMobile}
              />
            </div>
          </aside>
        </div>
      ) : null}
    </>
  )
}
